import { START_LOCATION, type Router } from "vue-router";
import { ApiErrorCode, isApiResponseError } from "tf-api-client";
import api from "../api";
import { useAuthStore } from "../stores/auth.store";
import { invalidateOrgSubscription } from "../composables/use-org-subscription";
import { invalidateOrgStatus } from "../composables/use-org-status";
import { sessionEvents } from "./session-events";

export const SUSPENDED_ROUTE = "/suspended";
// Card step of a self-service trial. Not `/onboarding`: that path is the
// account-activation page linked from the backend's emails.
export const ONBOARDING_CARD_ROUTE = "/onboarding/card";
// Query parameter the payment step sends the app home with, for a one-time
// notice: the "trial started" toast (`trial`), the "subscription active"
// toast (`paid`, Checkout without a trial), or the dialog for a card that
// already had a trial, so the paid subscription started instead (`cardUsed`).
export const ONBOARDING_DONE_PARAM = "onboarded";
export type OnboardingOutcome = "trial" | "paid" | "cardUsed";

// After a refresh that left the token unrestricted, a 403 `organization_frozen`
// means a specific organization is frozen, not the session. Repeated 403s from
// that organization (every click on a disabled action) must not each trigger
// another refresh, so the token is only re-verified once per cooldown.
const REFRESH_COOLDOWN_MS = 60_000;

const orgIdFromEndpoint = (endpoint: string): string | null =>
  endpoint.match(/^\/org\/([^/]+)/)?.[1] ?? null;

// Wires the SDK's session events (src/api.ts) to the store and the router.
// Call once, after pinia is installed. Safe to call before the router is
// installed: until the first navigation the router guard does the redirecting.
export function registerSessionHandler(router: Router) {
  const auth = useAuthStore();

  let lastRefreshAt = 0;
  let resolving: Promise<void> | null = null;

  const routerStarted = () => router.currentRoute.value !== START_LOCATION;

  const goToSuspended = () => {
    if (routerStarted() && router.currentRoute.value.path !== SUSPENDED_ROUTE) {
      router.replace(SUSPENDED_ROUTE);
    }
  };

  const goToOnboarding = () => {
    if (
      routerStarted() &&
      router.currentRoute.value.path !== ONBOARDING_CARD_ROUTE
    ) {
      router.replace(ONBOARDING_CARD_ROUTE);
    }
  };

  const signOut = (reason: string) => {
    auth.logout(reason);
    if (routerStarted()) {
      router.replace("/login");
    }
  };

  // Refresh failures (the SDK already de-duplicates them), plan section 4.5:
  //  - `organization_frozen`: the user is not entitled even to the billing
  //    view (standard user, or a Managed org): sign out with that message.
  //  - anything else, network errors and 5xx included: sign out, "session
  //    expired". Without a working refresh every later call fails as well.
  const onRefreshFailed = (error: Error) => {
    if (
      isApiResponseError(error) &&
      error.is(ApiErrorCode.OrganizationFrozen)
    ) {
      signOut(ApiErrorCode.OrganizationFrozen);
    } else if (auth.isLoggedIn()) {
      signOut("session_expired");
    }
  };

  // Decision table for a 403 `organization_frozen` on an authenticated call.
  // Debounced so concurrent failures run it once.
  const resolveOrganizationFrozen = (endpoint: string): Promise<void> => {
    if (auth.suspended) {
      goToSuspended();
      return Promise.resolve();
    }

    if (!resolving) {
      resolving = (async () => {
        if (Date.now() - lastRefreshAt >= REFRESH_COOLDOWN_MS) {
          try {
            await api.refreshSession();
          } catch {
            // `refreshFailed` already signed the user out when that applies
            return;
          }

          // the `tokensRefreshed` listener has stored the new token by now
          if (auth.suspended) {
            goToSuspended();
            return;
          }
        }

        // The token is unrestricted: the 403 belongs to one organization
        // (e.g. a frozen Managed org viewed by its MSP admin). Leave the
        // session alone.
        const orgId = orgIdFromEndpoint(endpoint);
        if (orgId) {
          auth.markOrgFrozen(orgId);
          // its banner shows at once; the reason follows with the refetch
          invalidateOrgStatus(orgId);
          void sessionEvents.emit({ type: "orgSuspendedAction", orgId });
        }
      })().finally(() => {
        resolving = null;
      });
    }

    return resolving;
  };

  // 403 `organization_onboarding_incomplete`: refresh once (debounced), then
  //  - still onboarding: go to the card step;
  //  - unrestricted now (the card was confirmed in another tab): retry
  //    nothing, reload the current route so it refetches with the new token.
  // The refresh is not skipped when the token is already an onboarding one:
  // it may be stale if the card was confirmed in another tab.
  let resolvingOnboarding: Promise<void> | null = null;
  const resolveOnboardingIncomplete = (): Promise<void> => {
    if (!resolvingOnboarding) {
      resolvingOnboarding = (async () => {
        const wasOnboarding = auth.onboarding;
        try {
          await api.refreshSession();
        } catch {
          // `refreshFailed` already signed the user out when that applies
          return;
        }

        // the `tokensRefreshed` listener has stored the new token (and
        // redirected to the card step if it is still an onboarding one).
        // Reload only when the refresh lifted the restriction: if the token
        // was never an onboarding one, reloading would loop.
        if (wasOnboarding && !auth.onboarding && routerStarted()) {
          window.location.reload();
        }
      })().finally(() => {
        resolvingOnboarding = null;
      });
    }

    return resolvingOnboarding;
  };

  return sessionEvents.on(async (event) => {
    switch (event.type) {
      case "tokensRefreshed":
        lastRefreshAt = Date.now();
        await auth.applyRefreshedTokens(event.accessToken);
        {
          // a restricted session fetches nothing but its own screen
          const refetch = !auth.onboarding && !auth.suspended;
          invalidateOrgSubscription(undefined, { refetch });
          invalidateOrgStatus(undefined, { refetch });
        }
        if (auth.onboarding) {
          goToOnboarding();
        } else if (auth.suspended) {
          goToSuspended();
        }
        return;

      case "refreshFailed":
        onRefreshFailed(event.error);
        return;

      case "unauthenticated":
        if (auth.isLoggedIn()) {
          signOut("session_expired");
        }
        return;

      case "organizationFrozen":
        // not awaited: the failing call should reject right away
        void resolveOrganizationFrozen(event.endpoint);
        return;

      case "onboardingIncomplete":
        // not awaited: the failing call should reject right away
        void resolveOnboardingIncomplete();
        return;

      case "orgSuspendedAction":
        // for the app shell
        return;

      case "organizationSelected":
        invalidateOrgSubscription(event.organizationId);
        invalidateOrgStatus(event.organizationId);
        return;
    }
  });
}
